import { SopAPI } from './api';
import { htmlToBlocks } from '../components/DocEditor/utils/htmlToBlocks';
import { MISSION_NOTES_TAG, ADMIN_ONLY_TAG } from './docTags';

/**
 * Mission doc migration — "Mission Docs Become Real Docs".
 *
 * Turns a mission's embedded content (block trees `task.docUser`/`task.docAdmin`,
 * or the older TipTap HTML `task.notes`/`task.internalNotes` for missions that
 * never opened the block tabs) into real Doc records attached via
 * `task.attachedDocs`.
 *
 * COPY, NEVER MOVE: the legacy fields are read here and never written again —
 * they stay frozen on the task as a permanent backup. Rolling back the code
 * rolls back the feature with zero data loss. `task.docsMigratedAt` (set by
 * the caller in the same sprint update as `attachedDocs`) prevents re-runs;
 * if a created doc fails to get attached (e.g. the sprint save is beaten by
 * an app close), the worst case is a stray duplicate doc in the library —
 * never lost content.
 *
 * Only trees with REAL content produce docs (a lone empty text block — the
 * editor's default — creates nothing), so the library doesn't fill with
 * blanks. Migrated docs are tagged `mission-notes` (library hides them by
 * default) and admin-tier content additionally `admin-only`.
 */

/** Plain text of a block's HTML `content` string. */
const contentText = (html) => {
  if (!html) return '';
  const el = document.createElement('div');
  el.innerHTML = html;
  return (el.textContent || '').replace(/\u00a0/g, ' ').trim();
};

/** True if any block in the tree carries actual text. */
export const treeHasRealContent = (tree) => {
  if (!Array.isArray(tree)) return false;
  return tree.some(
    (block) =>
      contentText(block?.content).length > 0 ||
      treeHasRealContent(block?.children)
  );
};

/** True if an HTML string carries any visible text. */
const htmlHasRealContent = (html) => contentText(html).length > 0;

/**
 * Pick the migration source for one tier: the block tree if it has content,
 * else the old HTML converted to blocks (missions that never opened the
 * block tabs), else null.
 */
const sourceTreeForTier = (tree, legacyHtml) => {
  if (treeHasRealContent(tree)) return tree;
  if (legacyHtml) {
    const converted = htmlToBlocks(legacyHtml);
    if (treeHasRealContent(converted)) return converted;
  }
  return null;
};

/**
 * True if a mission still carries legacy embedded content (block trees
 * `docUser`/`docAdmin` or old HTML `notes`/`internalNotes`) worth migrating.
 * Drives the manual "Transfer / Clear old notes" control — no auto-migration.
 */
export const hasLegacyMissionContent = (task) => {
  if (!task) return false;
  return Boolean(
    treeHasRealContent(task.docUser) ||
      treeHasRealContent(task.docAdmin) ||
      htmlHasRealContent(task.notes) ||
      htmlHasRealContent(task.internalNotes)
  );
};

/**
 * Create Doc records for a mission's embedded content.
 * Returns the `attachedDocs` entries to add — [] when there was nothing real
 * to migrate (the caller still marks the task migrated so we never re-scan).
 */
export async function migrateMissionDocs(task) {
  const entries = [];
  const title = (task.name || '').trim() || 'Untitled Mission';

  const userTree = sourceTreeForTier(task.docUser, task.notes);
  if (userTree) {
    const doc = await SopAPI.create({
      title,
      icon: task.icon || '📄',
      body: userTree,
      tags: [MISSION_NOTES_TAG],
    });
    entries.push({
      docId: doc.id,
      title: (doc.title || '').trim() || 'Untitled Doc',
      icon: doc.icon || '📄',
    });
  }

  const adminTree = sourceTreeForTier(task.docAdmin, task.internalNotes);
  if (adminTree) {
    const doc = await SopAPI.create({
      title: `${title} (Admin)`,
      icon: task.icon || '📄',
      body: adminTree,
      tags: [MISSION_NOTES_TAG, ADMIN_ONLY_TAG],
    });
    entries.push({
      docId: doc.id,
      title: (doc.title || '').trim() || 'Untitled Doc',
      icon: doc.icon || '📄',
    });
  }

  return entries;
}
